import * as RadixDialog from "@radix-ui/react-dialog";
import type { ReactNode } from "react";

import { nl } from "../i18n/nl";
import { IconButton } from "./Button";
import styles from "./Dialog.module.css";
import { CloseIcon } from "./icons";

export interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  children: ReactNode;
  /** `sheet` is the mobile bottom sheet with a grabber; `dialog` is centered. */
  variant?: "dialog" | "sheet";
  /** `wide` for content with two columns (search with preview). */
  size?: "default" | "wide";
}

export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  variant = "dialog",
  size = "default",
}: DialogProps) {
  const isSheet = variant === "sheet";
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className={styles.overlay} />
        <RadixDialog.Content
          className={isSheet ? styles.sheet : [styles.dialog, size === "wide" && styles.wide].filter(Boolean).join(" ")}
          {...(description ? {} : { "aria-describedby": undefined })}
        >
          {isSheet && <div className={styles.grabber} aria-hidden="true" />}
          <div className={styles.header}>
            <RadixDialog.Title className={styles.title}>{title}</RadixDialog.Title>
            <RadixDialog.Close asChild>
              <IconButton label={nl.common.close} icon={<CloseIcon />} className={styles.close} />
            </RadixDialog.Close>
          </div>
          {description && (
            <RadixDialog.Description className={styles.description}>
              {description}
            </RadixDialog.Description>
          )}
          {children}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

/** Mobile bottom sheet: max 84 % of the screen, backdrop closes it. */
export function Sheet(props: Omit<DialogProps, "variant">) {
  return <Dialog {...props} variant="sheet" />;
}
